import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import CalorieProgressRing from './components/CalorieProgressRing';
import MealSection from './components/MealSection';
import WaterIntakeTracker from './components/WaterIntakeTracker';
import NutritionChart from './components/NutritionChart';
import QuickStats from './components/QuickStats';
import SavedMealsPanel from './components/SavedMealsPanel';
import CalorieComparisonChart from './components/CalorieComparisonChart';
import PageTransition from '../../components/PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { nutritionService } from '../../services/trackingService';
import { savedMealsService } from '../../services/savedMealsService';
import { supabase } from '../../lib/supabase';
import milestoneService from '../../services/milestoneService';
import MilestonePopup from '../../components/MilestonePopup';
import { useDashboardMetrics } from '../../hooks/useRealtimeMetrics';
import { waterService } from '../../services/trackingService';
import { getUserProfile } from '../../services/profileService';
import Icon from '../../components/AppIcon';


const NutritionTracking = () => {
  const { user } = useAuth();
  const [nutritionData, setNutritionData] = useState({
    dailyCalorieGoal: 2000,
    caloriesConsumed: 0,
    caloriesBurned: 450,
    waterIntake: 1750,
    waterGoal: 2500,
    meals: {
      breakfast: [],
      lunch: [],
      dinner: [],
      snacks: []
    }
  });

  // User macro targets from profile
  const [macroTargets, setMacroTargets] = useState({ protein: 25, carbs: 50, fat: 25 });

  // Derive active macro preset from macroTargets
  const MACRO_PRESETS = {
    'muscle-gain': { protein: 35, carbs: 45, fat: 20 },
    'fat-loss': { protein: 40, carbs: 25, fat: 35 },
    'balanced': { protein: 25, carbs: 50, fat: 25 }
  };

  const PRESET_MEAL_SUGGESTIONS = {
    'muscle-gain': {
      label: 'Muscle Gain',
      icon: 'Dumbbell',
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      tagBg: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
      meals: [
        { name: 'Grilled Chicken Breast + Brown Rice', calories: 520, protein: 48, carbs: 52, fat: 8, meal: 'Lunch' },
        { name: 'Greek Yogurt + Whey Protein Shake', calories: 310, protein: 42, carbs: 22, fat: 5, meal: 'Breakfast' },
        { name: 'Salmon Fillet + Sweet Potato', calories: 480, protein: 38, carbs: 44, fat: 12, meal: 'Dinner' },
        { name: 'Cottage Cheese + Almonds', calories: 220, protein: 22, carbs: 8, fat: 12, meal: 'Snacks' }
      ]
    },
    'fat-loss': {
      label: 'Fat Loss',
      icon: 'Flame',
      color: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      tagBg: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
      meals: [
        { name: 'Egg White Omelette + Spinach', calories: 280, protein: 34, carbs: 8, fat: 10, meal: 'Breakfast' },
        { name: 'Turkey Lettuce Wraps', calories: 320, protein: 38, carbs: 12, fat: 14, meal: 'Lunch' },
        { name: 'Grilled Tilapia + Steamed Broccoli', calories: 350, protein: 42, carbs: 14, fat: 10, meal: 'Dinner' },
        { name: 'Celery + Peanut Butter (1 tbsp)', calories: 130, protein: 5, carbs: 6, fat: 9, meal: 'Snacks' }
      ]
    },
    'balanced': {
      label: 'Balanced',
      icon: 'Scale',
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      tagBg: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
      meals: [
        { name: 'Oatmeal + Banana + Boiled Eggs', calories: 420, protein: 22, carbs: 58, fat: 10, meal: 'Breakfast' },
        { name: 'Quinoa Bowl + Grilled Chicken', calories: 490, protein: 32, carbs: 52, fat: 14, meal: 'Lunch' },
        { name: 'Beef Stir-fry + Jasmine Rice', calories: 510, protein: 30, carbs: 54, fat: 16, meal: 'Dinner' },
        { name: 'Mixed Nuts + Apple', calories: 200, protein: 5, carbs: 22, fat: 12, meal: 'Snacks' }
      ]
    }
  };

  const getActivePreset = (targets) => {
    return Object.entries(MACRO_PRESETS)?.find(([, v]) =>
      v?.protein === targets?.protein && v?.carbs === targets?.carbs && v?.fat === targets?.fat
    )?.[0] || null;
  };

  const [quickStats, setQuickStats] = useState({
    mealsLogged: 0,
    avgCalories: 0,
    streakDays: 0,
    goalAchievement: 0
  });

  const [weeklyChartData, setWeeklyChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [waterSyncStatus, setWaterSyncStatus] = useState('synced');
  
  // Water intake state from real DB
  const [waterIntakeMl, setWaterIntakeMl] = useState(0);
  const [waterGoalMl, setWaterGoalMl] = useState(2000);
  const [waterLoading, setWaterLoading] = useState(true);
  
  // Milestone state
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);
  
  // Add missing state declarations
  const [showSavedMeals, setShowSavedMeals] = useState(false);
  const [savedMealSuccess, setSavedMealSuccess] = useState('');
  const [savedMealLoadError, setSavedMealLoadError] = useState(false);
  
  // Use real-time metrics for milestone detection
  const { metrics } = useDashboardMetrics(user?.id);

  // Load user profile macro targets
  useEffect(() => {
    const loadMacroTargets = async () => {
      if (!user?.id) return;
      try {
        const profile = await getUserProfile(user?.id);
        if (profile?.macroTargets) {
          setMacroTargets(profile?.macroTargets);
        }
        if (profile?.dailyCaloriesGoal) {
          setNutritionData(prev => ({ ...prev, dailyCalorieGoal: profile?.dailyCaloriesGoal }));
        }
      } catch (err) {
        console.error('Error loading macro targets:', err);
      }
    };
    loadMacroTargets();
  }, [user?.id]);

  // Check for calorie and water milestones
  useEffect(() => {
    if (!user?.id || !metrics) return;

    // Check calorie milestone
    if (metrics?.calories?.current) {
      const calorieMilestone = milestoneService?.checkMilestone(user?.id, 'calories', metrics?.calories?.current);
      if (calorieMilestone) {
        setCurrentMilestone(calorieMilestone);
        setShowMilestonePopup(true);
        milestoneService?.createMilestoneNotification(user?.id, calorieMilestone);
        return;
      }
    }

    // Check water milestone
    if (metrics?.water?.current) {
      const waterMilestone = milestoneService?.checkMilestone(user?.id, 'water', metrics?.water?.current);
      if (waterMilestone) {
        setCurrentMilestone(waterMilestone);
        setShowMilestonePopup(true);
        milestoneService?.createMilestoneNotification(user?.id, waterMilestone);
      }
    }
  }, [metrics?.calories?.current, metrics?.water?.current, user?.id]);

  useEffect(() => {
    document.title = 'Nutrition Tracking - PrimeOne+';
  }, []);

  // Fetch weekly nutrition logs for chart and quick stats
  useEffect(() => {
    const fetchWeeklyData = async () => {
      if (!user?.id) return;

      try {
        setChartLoading(true);
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo?.setDate(today?.getDate() - 6);
        const startDate = sevenDaysAgo?.toISOString()?.split('T')?.[0];
        const endDate = today?.toISOString()?.split('T')?.[0];

        const weeklyLogs = await nutritionService?.getNutritionLogs(user?.id, startDate, endDate);

        // Build day-by-day aggregation for last 7 days
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const aggregated = {};
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d?.setDate(today?.getDate() - i);
          const dateStr = d?.toISOString()?.split('T')?.[0];
          aggregated[dateStr] = {
            day: dayNames?.[d?.getDay()],
            calories: 0,
            protein: 0,
            carbs: 0,
            fat: 0
          };
        }

        weeklyLogs?.forEach(log => {
          const dateStr = log?.logged_date;
          if (aggregated?.[dateStr]) {
            aggregated[dateStr].calories += log?.calories || 0;
            aggregated[dateStr].protein += parseFloat(log?.protein || 0);
            aggregated[dateStr].carbs += parseFloat(log?.carbs || 0);
            aggregated[dateStr].fat += parseFloat(log?.fats || 0);
          }
        });

        const chartData = Object.values(aggregated)?.map(d => ({
          ...d,
          calories: Math.round(d?.calories),
          protein: Math.round(d?.protein),
          carbs: Math.round(d?.carbs),
          fat: Math.round(d?.fat)
        }));

        setWeeklyChartData(chartData);

        // Compute quick stats from weekly data
        const daysWithLogs = Object.values(aggregated)?.filter(d => d?.calories > 0);
        const totalMealsLogged = weeklyLogs?.length || 0;
        const avgCalories = daysWithLogs?.length > 0
          ? Math.round(daysWithLogs?.reduce((sum, d) => sum + d?.calories, 0) / daysWithLogs?.length)
          : 0;

        // Streak: count consecutive days ending today with logs
        const sortedDates = Object.keys(aggregated)?.sort();
        let streak = 0;
        for (let i = sortedDates?.length - 1; i >= 0; i--) {
          if (aggregated?.[sortedDates?.[i]]?.calories > 0) {
            streak++;
          } else {
            break;
          }
        }

        // Goal achievement: % of days this week where calories >= 80% of goal
        const calorieGoal = 2000;
        const daysAchieved = daysWithLogs?.filter(d => d?.calories >= calorieGoal * 0.8)?.length;
        const goalAchievement = daysWithLogs?.length > 0
          ? Math.round((daysAchieved / 7) * 100)
          : 0;

        setQuickStats({
          mealsLogged: totalMealsLogged,
          avgCalories,
          streakDays: streak,
          goalAchievement
        });
      } catch (error) {
        console.error('Error fetching weekly nutrition data:', error);
      } finally {
        setChartLoading(false);
      }
    };

    fetchWeeklyData();
  }, [user?.id]);

  // Fetch today's nutrition logs from Supabase
  useEffect(() => {
    const fetchNutritionData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const today = new Date()?.toISOString()?.split('T')?.[0];
        const logs = await nutritionService?.getNutritionLogs(user?.id, today, today);
        
        // Calculate total calories, protein, and carbs
        const totalCalories = logs?.reduce((sum, log) => sum + (log?.calories || 0), 0);
        const totalProtein = logs?.reduce((sum, log) => sum + parseFloat(log?.protein || 0), 0);
        const totalCarbs = logs?.reduce((sum, log) => sum + parseFloat(log?.carbs || 0), 0);
        const totalFat = logs?.reduce((sum, log) => sum + parseFloat(log?.fats || 0), 0);
        
        // Group by meal type and map database fields to component props
        const mapLogToFood = (log) => ({
          id: log?.id,
          name: log?.food_name,
          food_name: log?.food_name,
          calories: log?.calories,
          protein: parseFloat(log?.protein || 0),
          carbs: parseFloat(log?.carbs || 0),
          fat: parseFloat(log?.fats || 0),
          quantity: parseFloat(log?.serving_size) * 100 || 100,
          unit: 'g',
          syncStatus: log?.sync_status || 'synced'
        });

        const mealsByType = {
          breakfast: logs?.filter(log => log?.meal_type === 'breakfast')?.map(mapLogToFood),
          lunch: logs?.filter(log => log?.meal_type === 'lunch')?.map(mapLogToFood),
          dinner: logs?.filter(log => log?.meal_type === 'dinner')?.map(mapLogToFood),
          snacks: logs?.filter(log => log?.meal_type === 'snack')?.map(mapLogToFood)
        };

        setNutritionData(prev => ({
          ...prev,
          caloriesConsumed: totalCalories,
          proteinConsumed: totalProtein,
          carbsConsumed: totalCarbs,
          fatConsumed: totalFat,
          meals: mealsByType
        }));
      } catch (error) {
        console.error('Error fetching nutrition data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNutritionData();

    // Subscribe to real-time changes
    const nutritionChannel = supabase?.channel('nutrition_realtime')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'nutrition_logs',
        filter: `user_id=eq.${user?.id}`
      },
      () => {
        fetchNutritionData();
      }
    )?.subscribe();

    return () => {
      supabase?.removeChannel(nutritionChannel);
    };
  }, [user?.id]);

  const calculateMealCalories = (foods) => {
    return foods?.reduce((total, food) => total + food?.calories, 0);
  };

  const handleAddFood = async (mealType, foodData) => {
    if (!user?.id) return;

    try {
      // Map meal type to database enum values (lowercase English)
      const mealTypeMap = {
        'Breakfast': 'breakfast',
        'Lunch': 'lunch',
        'Dinner': 'dinner',
        'Snacks': 'snack'
      };

      // Optimistically add food with syncing status
      const tempFood = {
        id: `temp-${Date.now()}`,
        name: foodData?.food_name || foodData?.name || 'Alimento',
        food_name: foodData?.food_name || foodData?.name || 'Alimento',
        calories: foodData?.calories,
        protein: foodData?.protein || 0,
        carbs: foodData?.carbs || 0,
        fat: foodData?.fat || 0,
        quantity: (foodData?.portionMultiplier || 1.0) * 100,
        unit: 'g',
        syncStatus: 'syncing'
      };

      setNutritionData(prev => ({
        ...prev,
        meals: {
          ...prev?.meals,
          [mealType?.toLowerCase()]: [...(prev?.meals?.[mealType?.toLowerCase()] || []), tempFood]
        },
        caloriesConsumed: prev?.caloriesConsumed + (foodData?.calories || 0),
        proteinConsumed: (prev?.proteinConsumed || 0) + (foodData?.protein || 0),
        carbsConsumed: (prev?.carbsConsumed || 0) + (foodData?.carbs || 0),
        fatConsumed: (prev?.fatConsumed || 0) + (foodData?.fat || 0)
      }));

      const newLog = await nutritionService?.createNutritionLog(user?.id, {
        mealType: mealTypeMap?.[mealType] || 'snack',
        foodName: foodData?.food_name || foodData?.name || 'Alimento',
        calories: foodData?.calories,
        protein: foodData?.protein || 0,
        carbs: foodData?.carbs || 0,
        fat: foodData?.fat || 0,
        fiber: foodData?.fiber || 0,
        portionMultiplier: foodData?.portionMultiplier || 1.0,
        date: new Date()?.toISOString()?.split('T')?.[0]
      });

      // Update with real data and synced status
      setNutritionData(prev => ({
        ...prev,
        meals: {
          ...prev?.meals,
          [mealType?.toLowerCase()]: prev?.meals?.[mealType?.toLowerCase()]?.map(food => 
            food?.id === tempFood?.id ? {
              ...food,
              id: newLog?.id,
              syncStatus: 'synced'
            } : food
          )
        }
      }));

      // Refresh weekly chart data after adding food
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo?.setDate(today?.getDate() - 6);
      const weeklyLogs = await nutritionService?.getNutritionLogs(
        user?.id,
        sevenDaysAgo?.toISOString()?.split('T')?.[0],
        today?.toISOString()?.split('T')?.[0]
      );
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const aggregated = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d?.setDate(today?.getDate() - i);
        const dateStr = d?.toISOString()?.split('T')?.[0];
        aggregated[dateStr] = { day: dayNames?.[d?.getDay()], calories: 0, protein: 0, carbs: 0, fat: 0 };
      }
      weeklyLogs?.forEach(log => {
        const dateStr = log?.logged_date;
        if (aggregated?.[dateStr]) {
          aggregated[dateStr].calories += log?.calories || 0;
          aggregated[dateStr].protein += parseFloat(log?.protein || 0);
          aggregated[dateStr].carbs += parseFloat(log?.carbs || 0);
          aggregated[dateStr].fat += parseFloat(log?.fats || 0);
        }
      });
      setWeeklyChartData(Object.values(aggregated)?.map(d => ({
        ...d,
        calories: Math.round(d?.calories),
        protein: Math.round(d?.protein),
        carbs: Math.round(d?.carbs),
        fat: Math.round(d?.fat)
      })));
    } catch (error) {
      console.error('Error adding food:', {
        message: error?.message,
        details: error?.details || '',
        hint: error?.hint || '',
        code: error?.code || ''
      });
      
      // Update sync status to error
      setNutritionData(prev => ({
        ...prev,
        meals: {
          ...prev?.meals,
          [mealType?.toLowerCase()]: prev?.meals?.[mealType?.toLowerCase()]?.map(food => 
            food?.id?.toString()?.startsWith('temp-') ? {
              ...food,
              syncStatus: 'error'
            } : food
          )
        }
      }));
      
      alert(`Error al agregar alimento: ${error?.message || 'Error desconocido'}. Por favor verifica tu conexión e intenta de nuevo.`);
    }
  };

  const handleSaveMeal = async (collectionData) => {
    if (!user?.id) return;
    try {
      await savedMealsService?.createCollection(user?.id, collectionData);
      setSavedMealSuccess(`"${collectionData?.name}" guardado correctamente`);
      setTimeout(() => setSavedMealSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving meal collection:', err);
      throw err;
    }
  };

  const handleQuickAddCollection = async (collection) => {
    if (!user?.id) return;
    const items = collection?.saved_meal_items || [];
    const mealTypeMap = {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
      snack: 'Snacks',
      snacks: 'Snacks'
    };
    const uiMealType = mealTypeMap?.[collection?.meal_type] || 'Snacks';

    for (const item of items) {
      await handleAddFood(uiMealType, {
        food_name: item?.food_name,
        name: item?.food_name,
        calories: item?.calories || 0,
        protein: parseFloat(item?.protein || 0),
        carbs: parseFloat(item?.carbs || 0),
        fat: parseFloat(item?.fat || 0),
        fiber: parseFloat(item?.fiber || 0),
        portionMultiplier: 1.0
      });
    }
  };

  const handleAddWater = async (amount) => {
    if (!user?.id || !amount || amount <= 0) return;
    try {
      setWaterSyncStatus('syncing');
      // Optimistically update UI
      setWaterIntakeMl(prev => prev + amount);
      // Persist to DB
      await waterService?.createWaterLog(user?.id, {
        amount,
        source: 'manual',
        date: new Date()?.toISOString()?.split('T')?.[0]
      });
      setWaterSyncStatus('synced');
    } catch (error) {
      console.error('Error adding water:', error);
      // Revert optimistic update
      setWaterIntakeMl(prev => Math.max(0, prev - amount));
      setWaterSyncStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <PageTransition>
        <main className="container mx-auto px-4 py-6 space-y-6">
          {/* Page Header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <motion.div
                className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl"
                whileHover={{ scale: 1.08, rotate: -5 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Icon name="Apple" size={24} className="text-primary" />
              </motion.div>
              <h1 className="text-3xl font-bold text-foreground">Nutrition Tracking</h1>
            </div>
            <p className="text-muted-foreground">
              Monitor your daily nutrition and stay on track with your health goals
            </p>
            {/* Saved Meals toggle button */}
            <div className="flex items-center justify-center mt-4">
              <motion.button
                onClick={() => setShowSavedMeals(v => !v)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                  showSavedMeals
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border text-foreground hover:border-primary/40 hover:bg-primary/5'
                }`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Icon name="Bookmark" size={16} />
                Comidas Guardadas
                {showSavedMeals ? (
                  <Icon name="ChevronUp" size={14} />
                ) : (
                  <Icon name="ChevronDown" size={14} />
                )}
              </motion.button>
            </div>
          </motion.div>

          {/* Saved Meals Panel */}
          <AnimatePresence>
            {showSavedMeals && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <SavedMealsPanel
                  userId={user?.id}
                  onQuickAdd={handleQuickAddCollection}
                  isOpen={showSavedMeals}
                  onClose={() => setShowSavedMeals(false)}
                  onLoadError={() => setSavedMealLoadError(true)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Save success toast */}
          <AnimatePresence>
            {savedMealSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-success text-success-foreground rounded-xl shadow-lg text-sm font-medium"
              >
                <Icon name="CheckCircle2" size={16} />
                {savedMealSuccess}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Saved meals load error toast */}
          <AnimatePresence>
            {savedMealLoadError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 bg-destructive text-destructive-foreground rounded-xl shadow-lg text-sm font-medium"
              >
                <Icon name="AlertCircle" size={16} />
                <span>Error al cargar comidas guardadas</span>
                <button
                  onClick={() => setSavedMealLoadError(false)}
                  className="ml-1 opacity-70 hover:opacity-100 transition-opacity"
                >
                  <Icon name="X" size={14} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          >
            <QuickStats stats={quickStats} />
          </motion.div>

          {/* Main Content Grid */}
          <motion.div
            className="grid lg:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Left Column - Calorie Progress & Water */}
            <div className="space-y-6">
              <CalorieProgressRing
                consumed={nutritionData?.caloriesConsumed}
                burned={nutritionData?.caloriesBurned}
                goal={nutritionData?.dailyCalorieGoal}
                protein={nutritionData?.proteinConsumed || 0}
                carbs={nutritionData?.carbsConsumed || 0}
                fat={nutritionData?.fatConsumed || 0}
                macroTargets={macroTargets}
              />
              
              <WaterIntakeTracker
                currentIntake={waterIntakeMl}
                dailyGoal={waterGoalMl}
                onAddWater={handleAddWater}
                syncStatus={waterSyncStatus}
              />
            </div>

            {/* Middle Column - Meals */}
            <div className="space-y-6">
              <MealSection
                mealType="Breakfast"
                foods={nutritionData?.meals?.breakfast}
                totalCalories={calculateMealCalories(nutritionData?.meals?.breakfast)}
                onAddFood={handleAddFood}
                onSaveMeal={handleSaveMeal}
              />
              
              <MealSection
                mealType="Lunch"
                foods={nutritionData?.meals?.lunch}
                totalCalories={calculateMealCalories(nutritionData?.meals?.lunch)}
                onAddFood={handleAddFood}
                onSaveMeal={handleSaveMeal}
              />
            </div>

            {/* Right Column - More Meals */}
            <div className="space-y-6">
              <MealSection
                mealType="Dinner"
                foods={nutritionData?.meals?.dinner}
                totalCalories={calculateMealCalories(nutritionData?.meals?.dinner)}
                onAddFood={handleAddFood}
                onSaveMeal={handleSaveMeal}
              />
              
              <MealSection
                mealType="Snacks"
                foods={nutritionData?.meals?.snacks}
                totalCalories={calculateMealCalories(nutritionData?.meals?.snacks)}
                onAddFood={handleAddFood}
                onSaveMeal={handleSaveMeal}
              />
            </div>
          </motion.div>

          {/* Nutrition Analytics */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <CalorieComparisonChart
              caloriesConsumed={nutritionData?.caloriesConsumed}
              caloriesBurned={nutritionData?.caloriesBurned}
              dailyCalorieGoal={nutritionData?.dailyCalorieGoal}
              protein={nutritionData?.proteinConsumed || 0}
              carbs={nutritionData?.carbsConsumed || 0}
              fat={nutritionData?.fatConsumed || 0}
            />
          </motion.div>

          {/* Macro Preset Meal Suggestions */}
          {(() => {
            const activePreset = getActivePreset(macroTargets);
            const suggestion = activePreset ? PRESET_MEAL_SUGGESTIONS?.[activePreset] : null;
            if (!suggestion) return null;
            return (
              <motion.div
                className={`rounded-xl p-6 border ${suggestion?.bg} ${suggestion?.border} elevation-1`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${suggestion?.bg}`}>
                    <Icon name={suggestion?.icon} size={18} className={suggestion?.color} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">
                      {suggestion?.label} Meal Suggestions
                    </h3>
                    <p className="text-xs text-muted-foreground">Recommended foods for your active macro preset</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {suggestion?.meals?.map((meal, idx) => (
                    <motion.div
                      key={idx}
                      className="bg-card rounded-lg p-3 border border-border/50 space-y-2"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25, delay: idx * 0.05 }}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-sm font-medium text-foreground leading-tight">{meal?.name}</p>
                        <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${suggestion?.tagBg}`}>
                          {meal?.meal}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-xs font-bold text-foreground">{meal?.calories} kcal</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">P {meal?.protein}g</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-blue-600 dark:text-blue-400">C {meal?.carbs}g</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-amber-600 dark:text-amber-400">F {meal?.fat}g</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                  <Icon name="Info" size={12} />
                  Change your macro preset in Profile Settings → Health Preferences to update these suggestions.
                </p>
              </motion.div>
            );
          })()}

          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35, ease: [0.4, 0, 0.2, 1] }}
          >
            <NutritionChart
              weeklyData={weeklyChartData}
              macroData={{
                protein: nutritionData?.proteinConsumed || 0,
                carbs: nutritionData?.carbsConsumed || 0,
                fat: nutritionData?.fatConsumed || 0
              }}
              loading={chartLoading}
            />
          </motion.div>

          {/* Daily Summary */}
          <motion.div
            className="bg-card rounded-xl p-6 elevation-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35, ease: [0.4, 0, 0.2, 1] }}
          >
            <h3 className="text-lg font-semibold text-foreground mb-4">Today's Summary</h3>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { value: nutritionData?.caloriesConsumed, label: 'Calories Consumed', color: 'text-primary', bg: 'bg-primary/5' },
                { value: `${Math.round(nutritionData?.proteinConsumed || 0)}g`, label: 'Protein', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' },
                { value: `${Math.round(nutritionData?.carbsConsumed || 0)}g`, label: 'Carbs', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-500/10' },
                { value: nutritionData?.caloriesBurned, label: 'Calories Burned', color: 'text-success', bg: 'bg-success/5' },
                { value: `${waterIntakeMl}ml`, label: 'Water Intake', color: 'text-accent', bg: 'bg-accent/5' },
                { value: Object.values(nutritionData?.meals)?.flat()?.length, label: 'Foods Logged', color: 'text-secondary', bg: 'bg-secondary/5' },
              ]?.map((stat, i) => (
                <motion.div
                  key={stat?.label}
                  className={`text-center p-4 ${stat?.bg} rounded-lg cursor-default`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.4 + i * 0.05 }}
                  whileHover={{ y: -2, scale: 1.02 }}
                >
                  <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  <p className="text-sm text-muted-foreground">{stat?.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </main>
      </PageTransition>
      <QuickActionButton />
      {/* Milestone Popup */}
      <MilestonePopup
        milestone={currentMilestone}
        isOpen={showMilestonePopup}
        onClose={() => setShowMilestonePopup(false)}
      />
    </div>
  );
};

export default NutritionTracking;